import {
  BitkitControlButton,
  BitkitExpandableCard,
  BitkitLink,
  BitkitList,
  BitkitSegmentedControl,
  BitkitSpinner,
  IconThumbDown,
  IconThumbUp,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

import CopiableInput from '@/components/CopiableInput/CopiableInput';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { RemoteAccessData } from '@/models/Build';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';

const DocumentationNote = () => (
  <>
    Available while build is running and for 10 minutes after it finishes.{' '}
    <BitkitLink
      colorVariant="purple"
      href="https://docs.bitrise.io/en/bitrise-ci/run-and-analyze-builds/build-data-and-troubleshooting/remote-access.html"
      isExternal
      onClick={(e: SyntheticEvent) => e.stopPropagation()}
    >
      See our documentation
    </BitkitLink>{' '}
    for details.
  </>
);

type RemoteAccessInstructionsProps = {
  buildSlug: string;
  appSlug: string;
  isBuildOnHold: boolean;
  remoteAccessData: RemoteAccessData;
};

type ConnectionMode = 'vscode' | 'ssh' | 'screenshare';

const RemoteAccessInstructions = ({
  buildSlug,
  appSlug,
  remoteAccessData,
  isBuildOnHold,
}: RemoteAccessInstructionsProps) => {
  const {
    account: { slug: accountSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { isMobile } = useResponsive();

  const defaultMode = 'vscode';
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>(defaultMode);

  const connectionModes = useMemo(() => {
    const modes: Array<{ key: ConnectionMode; label: string }> = [
      { key: 'vscode', label: 'VSCode' },
      { key: 'ssh', label: 'SSH' },
    ];
    if (remoteAccessData.screenshare) {
      modes.push({ key: 'screenshare', label: 'Screen share' });
    }
    return modes;
  }, [remoteAccessData.screenshare]);

  useEffect(() => {
    segmentTrack('Remote Access Instructions Tab Displayed', {
      build_slug: buildSlug,
      workspace_slug: accountSlug,
      app_slug: appSlug,
      tab_name: connectionMode,
      is_default_tab: connectionMode === defaultMode,
    });
  }, [connectionMode, buildSlug, defaultMode, accountSlug, appSlug]);

  return (
    <BitkitExpandableCard
      defaultExpanded
      elevation={false}
      marginBlockEnd="24"
      suffix={!isMobile && <DocumentationNote />}
      title={
        <Text as="span" textStyle="heading/h2">
          Remote Access
        </Text>
      }
    >
      <BitkitSegmentedControl
        value={connectionMode}
        onValueChange={({ value }) => setConnectionMode(value as ConnectionMode)}
        marginBlockEnd="16"
        size="lg"
        fullWidth
      >
        {connectionModes.map(({ key, label }) => (
          <BitkitSegmentedControl.Item key={key} value={key}>
            {label}
          </BitkitSegmentedControl.Item>
        ))}
      </BitkitSegmentedControl>
      <Box display="flex" flexDirection={['column', 'row']} gap="64">
        {connectionMode === 'vscode' && (
          <VSCodeConnectionInfo
            buildSlug={buildSlug}
            remoteAccessData={remoteAccessData}
            appSlug={appSlug}
            isBuildOnHold={isBuildOnHold}
          />
        )}
        {connectionMode === 'ssh' && (
          <SSHConnectionInfo
            buildSlug={buildSlug}
            remoteAccessData={remoteAccessData}
            appSlug={appSlug}
            isBuildOnHold={isBuildOnHold}
          />
        )}
        {connectionMode === 'screenshare' && (
          <ScreenshareConnectionInfo
            buildSlug={buildSlug}
            remoteAccessData={remoteAccessData}
            appSlug={appSlug}
            isBuildOnHold={isBuildOnHold}
          />
        )}
      </Box>
    </BitkitExpandableCard>
  );
};

const VSCodeConnectionInfo = ({
  remoteAccessData,
  buildSlug,
  appSlug,
  isBuildOnHold,
}: RemoteAccessInstructionsProps) => {
  const vscodeSnippet = `bitrise :remote vscode --host ${remoteAccessData.ssh.host} --port ${remoteAccessData.ssh.port} --user ${remoteAccessData.ssh.user} --password ${remoteAccessData.ssh.password}`;

  return (
    <>
      <Box display="flex" flexDirection="column" gap="24" flex="0.5">
        <Box display="flex" flexDirection={['column', 'row']} gap="32">
          <CopiableInput
            hideButtonText
            label="Install Bitrise CLI"
            value="brew install bitrise"
            helperText={
              <BitkitLink
                href="https://github.com/bitrise-io/bitrise/blob/master/README.md"
                colorVariant="purple"
                isExternal
              >
                Other ways to install
              </BitkitLink>
            }
            flexGrow="1"
          />
          <CopiableInput
            hideButtonText
            label="Install remote access plugin"
            value="bitrise plugin install https://github.com/bitrise-io/bitrise-remote-access-cli.git"
            flexGrow="1"
          />
        </Box>
        <CopiableInput hideButtonText label="Connect to this build machine" value={vscodeSnippet} />
        {isBuildOnHold && <BuildOnHoldInfo />}
      </Box>
      <Box display="flex" flex="0.5" flexDirection="column" gap="12" padding="24">
        <BitkitList>
          <BitkitList.Item>
            Access this environment from your local{' '}
            <BitkitLink href="https://code.visualstudio.com" isExternal colorVariant="purple">
              Visual Studio Code
            </BitkitLink>{' '}
            as a{' '}
            <BitkitLink
              href="https://code.visualstudio.com/docs/remote/remote-overview"
              isExternal
              colorVariant="purple"
            >
              remote workspace
            </BitkitLink>
          </BitkitList.Item>
          <BitkitList.Item>File editing and quick iteration from your IDE</BitkitList.Item>
          <BitkitList.Item>Integrated terminal</BitkitList.Item>
          <BitkitList.Item>VSCode extensions</BitkitList.Item>
        </BitkitList>
        <Feedback connectionMode="vscode" buildSlug={buildSlug} appSlug={appSlug} />
      </Box>
    </>
  );
};

const SSHConnectionInfo = ({ remoteAccessData, buildSlug, appSlug, isBuildOnHold }: RemoteAccessInstructionsProps) => (
  <>
    <Box display="flex" flexDirection="column" gap="24" flex={0.5}>
      <CopiableInput hideButtonText label="Command" value={remoteAccessData.ssh.command} />
      <CopiableInput hideButtonText label="Password" value={remoteAccessData.ssh.password} />
      {isBuildOnHold && <BuildOnHoldInfo />}
    </Box>
    <Box display="flex" flex="0.5" flexDirection="column" gap="12" padding="24">
      <BitkitList>
        <BitkitList.Item>Access this environment from your terminal through SSH</BitkitList.Item>
        <BitkitList.Item>No setup or additional install required</BitkitList.Item>
      </BitkitList>
      <Feedback connectionMode="ssh" buildSlug={buildSlug} appSlug={appSlug} />
    </Box>
  </>
);

const ScreenshareConnectionInfo = ({
  remoteAccessData,
  buildSlug,
  appSlug,
  isBuildOnHold,
}: RemoteAccessInstructionsProps) => (
  <>
    <Box display="flex" flexDirection="column" gap="24" flex={0.5}>
      <CopiableInput hideButtonText label="URL" value={remoteAccessData.screenshare?.address} />
      <Box display="flex" flexDirection={['column', 'row']} gap="32">
        <CopiableInput hideButtonText flexGrow={1} label="Username" value={remoteAccessData.screenshare?.username} />
        <CopiableInput hideButtonText flexGrow={1} label="Password" value={remoteAccessData.screenshare?.password} />
      </Box>
      {isBuildOnHold && <BuildOnHoldInfo />}
    </Box>
    <Box display="flex" flex="0.5" flexDirection="column" gap="12" padding="24">
      <BitkitList>
        <BitkitList.Item>
          Access this environment with remote desktop software (VNC), such as the built-in{' '}
          <BitkitLink
            href="https://support.apple.com/guide/mac-help/share-the-screen-of-another-mac-mh14066/mac"
            isExternal
            colorVariant="purple"
          >
            macOS Screen Sharing app
          </BitkitLink>
        </BitkitList.Item>
        <BitkitList.Item>Full desktop access to every running app</BitkitList.Item>
        <BitkitList.Item>Keyboard and mouse control</BitkitList.Item>
      </BitkitList>
      <Feedback connectionMode="screenshare" buildSlug={buildSlug} appSlug={appSlug} />
    </Box>
  </>
);

type FeedbackProps = {
  connectionMode: ConnectionMode;
  buildSlug: string;
  appSlug: string;
};

const Feedback = ({ connectionMode, buildSlug, appSlug }: FeedbackProps) => {
  const [postFeedback, setPostFeedback] = useState(false);

  const handleFeedback = (feedback: 'positive' | 'negative') => {
    segmentTrack('Remote Access Instructions Reaction Button Clicked', {
      build_slug: buildSlug,
      app_slug: appSlug,
      tab_name: connectionMode,
      reaction_type: feedback,
    });
    setPostFeedback(true);
  };

  return postFeedback ? (
    <Text textStyle="body/md/semibold">Thank you for your feedback!</Text>
  ) : (
    <Text textStyle="body/md/semibold">
      Do you find this feature useful?{' '}
      <BitkitControlButton
        icon={IconThumbUp}
        label="Useful"
        marginInlineStart="8"
        onClick={() => handleFeedback('positive')}
      />
      <BitkitControlButton icon={IconThumbDown} label="Not useful" onClick={() => handleFeedback('negative')} />
    </Text>
  );
};

const BuildOnHoldInfo = () => (
  <Box display="flex" alignItems="center" gap="8">
    <BitkitSpinner size="md" aria-hidden />
    <Text textStyle="body/md/regular">Initializing connection, please wait...</Text>
  </Box>
);

export default RemoteAccessInstructions;
